import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { User } from '@app/_models';
import { CartService } from './cart.service';
import { QuotationCartService } from './quotation.service';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from './currency.service';
import { CurrencyRateService } from './currency-rate.service';
import { ChatService } from './chat.service';
import { isPlatformBrowser } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<any>;
    private settingsSubject: BehaviorSubject<User>;
    public settings: Observable<any>;
    private currentZoneSubject: BehaviorSubject<any>;
    public currentZone: Observable<any>;
    public countriesSubject$: BehaviorSubject<any> = new BehaviorSubject(null);
    readonly countries$: Observable<
        any
    > = this.countriesSubject$.asObservable();
    private userSignal = signal(null);

    public set user(user) {
        this.userSignal.set(user);
    }

    public get user() {
        return this.userSignal.asReadonly();
    }

    private notificationsSignal = signal(null);

    public set notifications(notifications: any) {
        this.notificationsSignal.set(notifications);
    }

    public get notifications() {
        return this.notificationsSignal.asReadonly();
    }

    constructor(
        private http: HttpClient,
        private cart: CartService,
        private quotationCart: QuotationCartService,
        private translate: TranslateService,
        private chatService: ChatService,
        private currencyService: CurrencyService,
        private currencyRateService: CurrencyRateService,
        private toastr: ToastrService,
        private router: Router,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        this.currentUserSubject = new BehaviorSubject<User>(null);
        this.currentUser = this.currentUserSubject.asObservable();
        this.settingsSubject = new BehaviorSubject<User>(null);
        this.settings = this.settingsSubject.asObservable();
        this.currentZoneSubject = new BehaviorSubject<User>(null);
        this.currentZone = this.currentZoneSubject.asObservable();
        this.loadUserDetails();
    }

    public get currentUserValue(): any {
        return this.currentUserSubject.value;
    }

    public get currentZoneValue(): any {
        return this.currentZoneSubject.value;
    }

    getUserDetails() {
        if (isPlatformBrowser(this.platformId)) {
            return this.http.get<any>(
                `${environment.apiUrl}/users/get-details`,
                { withCredentials: true }
            );
        } else return of({});
    }

    login(params) {
        return this.http
            .post<any>(`${environment.apiUrl}/users/auth/login`, params)
            .pipe(
                map(response => {
                    // Country/currency remains unchanged on login
                    // The session preserves whatever country the guest had selected
                    this.loadUserDetails();
                    return response;
                })
            );
    }

    setUserZone(zone) {
        this.currentZoneSubject.next(zone);
    }

    forgetPassword(email) {
        return this.http.post<any>(
            `${environment.apiUrl}/users/auth/password/forget`,
            { email: email }
        );
    }

    resetPassword(key, password) {
        return this.http.post<any>(
            `${environment.apiUrl}/users/auth/password/reset`,
            { key: key, password: password }
        );
    }

    logout() {
        return this.http
            .get<any>(`${environment.apiUrl}/users/auth/logout`)
            .pipe(
                tap(data => {
                    // CRITICAL FIX: Don't clear currency cache on logout
                    // The backend now preserves country/currency in the new guest session
                    // So we keep the existing cache to avoid flicker
                    this.resetUser();
                    
                    // Reload user details to sync with the new guest session
                    // This ensures we get the preserved country/currency from backend
                    this.loadUserDetails();
                })
            );
    }

    resetUser() {
        this.user = null;
        this.notifications = null;
        this.currentUserSubject.next(null);
        this.cart.setCart([]);
        this.quotationCart.setCart([]);
        this.chatService.close();
    }
    loadUserDetails() {
        this.getUserDetails()
            .pipe(take(1))
            .subscribe(
                (response: any) => {
                    _.each(response?.carts, cart => {
                        if (!_.isEmpty(cart.warnings))
                            this.displayWarnings(cart.warnings);
                    });
                    this.cart.setCart(response.carts);
                    this.quotationCart.setCart(response.qcarts);
                    if (_.isEmpty(response.data)) {
                        response.data = null;
                    }

                    if (response.settings) {
                        this.settingsSubject.next(response.settings);
                    }
                    this.currentUserSubject.next(response.data);
                    this.user = response.data;
                    this.notifications = response.notifications || {};

                    // Country is NOT synced from backend here
                    // The frontend's localStorage/SharedService country is the source of truth
                    // Backend session country is set via the country interceptor headers

                    // Handle currency changes and rate loading
                    if (response.currencies) {
                        const oldCurrency = this.currencyService.options?.code;

                        let chosenCurrency =
                            _.find(response.currencies, {
                                _id: response.selectedCurrencyId
                            }) ??
                            _.find(response.currencies, { isDefault: true });

                        const newCurrency = chosenCurrency?.symbol;

                        // CRITICAL FIX: Update currency service options AND load rates synchronously
                        // This prevents the UI from showing the new currency before rates are ready

                        // Check if cached rates exist and match the new currency
                        const cachedRates = this.currencyRateService.getCachedRatesSync();
                        const cacheMatchesCurrency =
                            cachedRates?.baseCurrency === newCurrency;

                        if (cacheMatchesCurrency) {
                            // Rates already match - safe to update currency immediately
                            this.currencyService.options = {
                                code: newCurrency,
                                symbol: newCurrency
                            };
                            this.currencyService.isReady = true;
                        } else {
                            // Clear stale cache to prevent showing old currency briefly
                            this.currencyRateService.clearCache();

                            // Mark as NOT ready - pipe will show source currency until rates load
                            this.currencyService.isReady = false;

                            // Update to new currency immediately but mark as not ready
                            // This way the pipe knows to wait
                            this.currencyService.options = {
                                code: newCurrency,
                                symbol: newCurrency
                            };
                        }

                        this.currencyService.currenciesSubject$.next({
                            currencies: response.currencies,
                            selectedCurrencyId: response.selectedCurrencyId
                        });

                        // Load rates if currency changed OR if this is first load OR cache mismatch
                        if (
                            !oldCurrency ||
                            oldCurrency !== newCurrency ||
                            !cacheMatchesCurrency
                        ) {
                            this.currencyRateService.loadRates(true).subscribe({
                                next: ratesState => {
                                    // VERIFICATION: Ensure loaded rates match the currency we requested
                                    if (
                                        ratesState.baseCurrency !== newCurrency
                                    ) {
                                        // Clear cache and try again
                                        this.currencyRateService.clearCache();
                                        this.currencyService.isReady = false;
                                        this.currencyRateService
                                            .loadRates(true)
                                            .subscribe({
                                                next: () => {
                                                    this.currencyService.isReady = true;
                                                }
                                            });
                                    } else {
                                        // Rates loaded successfully - mark as ready
                                        this.currencyService.isReady = true;
                                    } 
                                },
                                error: error => {
                                    console.error(
                                        '[Auth] ✗ Failed to load rates:',
                                        error
                                    );

                                    // Show error message to user (but not on maintenance page)
                                    if (!this.router.url.includes('/maintenance')) {
                                        const errorMsg =
                                            this.translate.currentLang === 'ar'
                                                ? 'فشل تحميل أسعار الصرف. سيتم عرض الأسعار بالعملة الأصلية.'
                                                : 'Failed to load exchange rates. Prices will be shown in original currency.';

                                        this.toastr.error(errorMsg, '', {
                                            timeOut: 5000,
                                            closeButton: true
                                        });
                                    }

                                    // Mark as ready even on error to show source currency
                                    this.currencyService.isReady = true;
                                }
                            });
                        }
                    }

                    this.setUserZone(response.zone ?? {});
                    /* if (response?.data?.preference?.language)
                this.translate.use(response?.data?.preference?.language); */
                },
                err => console.log(err)
            );
    }

    register(user) {
        return this.http.post<any>(
            `${environment.apiUrl}/users/auth/register`,
            user
        );
    }

    changePassword(newPassword, oldPassword) {
        return this.http.post<any>(
            `${environment.apiUrl}/users/auth/password/change`,
            { newPassword: newPassword, oldPassword: oldPassword }
        );
    }

    getCountries() {
        return this.http.get<any>(`${environment.apiUrl}/users/countries`).pipe(
            tap(countries => {
                this.countriesSubject$.next(countries);
            })
        );
    }

    getCountryCodes() {
        return this.http
            .get<any>(`${environment.apiUrl}/users/country-codes-currencies`)
            .pipe(
                tap(countries => {
                    this.countriesSubject$.next(countries);
                })
            );
    }

    getCompanies() {
        return this.http.get<any>(`${environment.apiUrl}/users/companies`);
    }

    displayWarnings(
        warnings: {
            type: string;
            title: { en: string; ar: string };
            description: { en: string; ar: string };
        }[]
    ) {
        const localize = (val: { en: string; ar: string }) =>
            val[this.translate?.currentLang ?? 'en'];
        _.each(warnings, warn => {
            this.toastr.warning(
                localize(warn.description),
                localize(warn.title)
            );
        });
    }

    updateNotifications({ removed = null, added = null }) {
        let notifications = this.notifications();
        if (!notifications) notifications = {};

        if (_.isArray(removed)) {
            _.forEach(removed, removedNotification => {
                notifications[removedNotification.module] = _.filter(
                    notifications[removedNotification.module],
                    notification => notification._id != removedNotification._id
                );
            });
        } else if (removed) {
            notifications[removed.module] = _.filter(
                notifications[removed.module],
                notification => {
                    return notification._id != removed._id;
                }
            );
        }

        if (added && _.isArray(added)) {
            _.forEach(added, addedNotification => {
                notifications[addedNotification.module] = _.concat(
                    notifications[addedNotification.module] || [],
                    added
                );
            });
        } else if (added) {
            notifications[added.module] = _.concat(
                notifications[added.module] || [],
                added
            );
        }

        notifications = _.pickBy(notifications, modules => {
            return !_.isEmpty(modules);
        });
        this.notifications = _.cloneDeep(notifications);
    }

    // Optimistically decrement the RFQ notification counter when user clicks an offered quotation
    // Removes one 'update' type notification from the 'quote' group immediately
    optimisticDecrementQuoteUpdate() {
        try {
            const current = this.notifications();
            if (!current || !Array.isArray(current.quote) || current.quote.length === 0) return;
            // Prefer removing an 'update' type notification first
            const idx = current.quote.findIndex((n: any) => (n?.type || '').toLowerCase() === 'update');
            if (idx >= 0) {
                current.quote.splice(idx, 1);
            } else {
                // Fallback: remove the last one
                current.quote.pop();
            }
            // Clean empty groups
            const cleaned = _.pickBy(current, (modules: any[]) => Array.isArray(modules) ? modules.length > 0 : !!modules);
            this.notifications = _.cloneDeep(cleaned);
        } catch {}
    }
}
